/* eslint-disable max-lines */
import { createSlice } from "@reduxjs/toolkit";
import { selectEditorPreviewWidgetComponentProps, selectEditorSettingsColumnsOptions, selectEditorSettingsData } from "./draft-selectors";
import { deleteManyColumnOptionStates, deleteManyColumnTemplates, removeManyColumnTemplatesFromGroup, removeManyDatapointRequests, removeManyDataSelectProps, sortColumnTemplates, updateEditorPreviewWidgetComponentProps, updateOrAddColumnOptionState, updateOrAddColumnTemplate, updateOrAddDatapointRequest, updateOrAddManyColumnOptionStates, updateOrAddManyColumnTemplates } from "./helpers";
import { defaultComparisonMeasureByEntity, WidgetWorkshopInitialState, WorkShopInitialStateByType } from "./initialState";
import { addColumnReducer, addSettingsGroupingOptionsDatapointsReducer, addSettingsGroupingOptionsReducer, removeColumnsReducer, moveColumnsReducer, setColumnInVariationReducer, setColumnOptionSortOrderReducer, unsetColumnInVariationReducer } from "./reducers";
import { applyGroupingOnColumnsReducer } from "./reducers/applyGroupingOnColumnsReducer";
import { clearCurrentFilterValuesReducer } from "./reducers/clearCurrentFilterValuesReducer";
import { removeSettingsGroupingOptionsDatapointsReducer } from "./reducers/removeSettingsGroupingOptionsDatapointsReducer";
import { setCurrentFilterValuesReducer } from "./reducers/setCurrentFilterValuesReducer";
import { setWorkshopComponentPropsReducer } from "./reducers/setWorkshopComponentPropsReducer";
import { unapplyGroupingOnColumnsReducer } from "./reducers/unapplyGroupingOnColumnsReducer";
import { DatapointKind } from "./types";
import { updateMeasuresAndBuilders } from "./utils";
import { getEnumValues } from "common/enum-helpers";
import { DetailsPanelType } from "components/modal/widget-workshop/types";
import { WidgetMode, componentPropsPatcher } from "store/current-dashboard/types";
const slice = createSlice({
    initialState: WidgetWorkshopInitialState,
    name: "widgetWorkshopReducers",
    reducers: {
        addColumn: addColumnReducer,
        addSettingsGroupingOptions: addSettingsGroupingOptionsReducer,
        addSettingsGroupingOptionsDatapoints: addSettingsGroupingOptionsDatapointsReducer,
        applyGroupingOnColumns: applyGroupingOnColumnsReducer,
        clearCurrentFilterValues: clearCurrentFilterValuesReducer,
        groupColumns: (state, action) => {
            if (!action.payload) {
                return;
            }
            const settingsData = selectEditorSettingsData(state);
            if (settingsData?.columns?.options) {
                settingsData.columns.options = [...action.payload];
            }
            updateOrAddManyColumnTemplates(state, action.payload);
        },
        moveColumns: (state, action) => {
            if (!action?.payload) {
                return;
            }
            const data = selectEditorSettingsData(state);
            if (data && data.columns?.options) {
                data.columns.options = action.payload;
                updateOrAddManyColumnTemplates(state, data.columns.options);
                moveColumnsReducer(state);
            }
        },
        removeColumns: (state, action) => {
            const columnsOptions = selectEditorSettingsColumnsOptions(state);
            const componentProps = selectEditorPreviewWidgetComponentProps(state);
            if (columnsOptions && componentProps && action?.payload) {
                removeManyDatapointRequests(state, action.payload);
                deleteManyColumnOptionStates(state, action.payload);
                deleteManyColumnTemplates(state, action.payload);
                removeManyDataSelectProps(state, action.payload);
                removeColumnsReducer(state);
            }
        },
        removeSettingsGroupingOptionsDatapoints: removeSettingsGroupingOptionsDatapointsReducer,
        resetWorkshopState: (state) => {
            state.editor = WidgetWorkshopInitialState.editor;
            state.header = WidgetWorkshopInitialState.header;
        },
        setBarDatapoinOptionName: (state, action) => {
            const optionState = selectEditorSettingsColumnsOptions(state)?.find((option) => option?.id === action?.payload?.id);
            if (optionState && action.payload?.name) {
                optionState.name = action.payload.name;
                const props = selectEditorPreviewWidgetComponentProps(state);
                if (props.dataSelectProps?.length) {
                    const [measures, dimensions] = props.dataSelectProps;
                    const select = optionState.datapointKind === DatapointKind.Measure ? measures.selectValues?.[optionState.id] : dimensions.selectValues?.[optionState.id];
                    if (select) {
                        select.displayName = optionState.name || "";
                    }
                }
                else {
                    const args = props?.pipelineParameter?.customVisitorsReducer?.find((c) => c.method === "highchartsOneMeasure")?.arguments?.[0];
                    if (args) {
                        if (optionState.datapointKind === DatapointKind.Measure) {
                            args.defaultLabel = optionState.name;
                        }
                        else if (optionState.datapointKind === DatapointKind.Dimension) {
                            args.dimensionLabel = optionState.name;
                        }
                    }
                }
            }
        },
        setColumnInVariation: setColumnInVariationReducer,
        setColumnOptionActiveAccordionTab: (state, action) => {
            const optionState = selectEditorSettingsColumnsOptions(state)?.find((option) => option?.id === action?.payload?.id);
            if (optionState) {
                optionState.activeAccordionTab = action.payload.activeAccordionTab;
            }
        },
        setColumnOptionHighchartsDisplayType: (state, action) => {
            const optionState = selectEditorSettingsColumnsOptions(state)?.find((option) => option?.id === action?.payload?.id);
            if (optionState) {
                optionState.highchartsDisplayType = action.payload?.highchartsDisplayType ?? "line";
            }
        },
        setColumnOptionIsChecked: (state, action) => {
            const optionState = selectEditorSettingsColumnsOptions(state)?.find((option) => option?.id === action?.payload?.id);
            if (optionState) {
                optionState.isChecked = action.payload?.isChecked ?? false;
            }
        },
        setColumnOptionName: (state, action) => {
            const optionState = selectEditorSettingsColumnsOptions(state)?.find((option) => option?.id === action?.payload?.id);
            if (optionState && action.payload?.name) {
                optionState.name = action.payload.name;
                updateOrAddColumnTemplate(state, optionState);
            }
        },
        setColumnOptionPmiIndex: (state, action) => {
            const optionState = selectEditorSettingsColumnsOptions(state)?.find((option) => option?.id === action?.payload?.id);
            if (optionState) {
                optionState.pmiIndex = action.payload.index;
                const props = selectEditorPreviewWidgetComponentProps(state);
                const patchers = props?.componentPropsPatchers || [];
                const patcherIndex = patchers.findIndex((p) => p.patcher === componentPropsPatcher.setupPublicIndicesContext) ?? -1;
                const patcher = patcherIndex < 0 ? { patcher: componentPropsPatcher.setupPublicIndicesContext } : patchers.splice(patcherIndex, 1)?.[0];
                if (optionState.pmiIndex) {
                    patcher.argument = { ...patcher.argument || {}, [optionState.id]: optionState.pmiIndex.toString() };
                }
                else if (patcher.argument) {
                    delete patcher.argument[optionState.id];
                }
                props.componentPropsPatchers = [...patchers || [], patcher];
            }
        },
        setColumnOptionSortOrder: setColumnOptionSortOrderReducer,
        setCurrentFilterValues: setCurrentFilterValuesReducer,
        setDetailsPanel: (state, action) => {
            if (action.payload) {
                if (action.payload.isOpen) {
                    const panelTypes = getEnumValues(DetailsPanelType);
                    panelTypes.forEach((type) => {
                        const panelState = state.editor?.detailPanels?.[type];
                        if (panelState) {
                            panelState.isOpen = false;
                        }
                    });
                }
                state.editor.detailPanels[action.payload.type] = action.payload;
            }
        },
        setDisplayOptionsExcludeSoldAssets: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            if (widgetComponentProps) {
                widgetComponentProps.userSettings = { ...widgetComponentProps.userSettings, excludeSoldAssets: action.payload };
            }
        },
        setDisplayOptionsTotals: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            if (widgetComponentProps) {
                (widgetComponentProps.userSettings.displayOptions ??= {}).showTotals = action.payload;
            }
        },
        setDisplayTotalRows: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            if (widgetComponentProps) {
                (widgetComponentProps.userSettings.displayOptions ??= {}).showTotalRows = action.payload;
            }
        },
        setShowNotification: (state, action) => {
            state.editor.showNotification = action.payload;
        },
        setSparkline: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            if (widgetComponentProps) {
                widgetComponentProps.sparkline = action.payload;
                if (!widgetComponentProps.pipelineParameter.datapointsVisitorsReducer?.find((x) => x.method === "sortByDatapoint")) {
                    widgetComponentProps.pipelineParameter.datapointsVisitorsReducer = [
                        {
                            arguments: [
                                "ReferenceDate",
                                "Desc"
                            ],
                            method: "sortByDatapoint"
                        }
                    ];
                }
                const optionState = selectEditorSettingsColumnsOptions(state)?.[0];
                if (optionState) {
                    updateOrAddDatapointRequest(state, { ...optionState, history: action.payload !== undefined });
                    updateOrAddColumnOptionState(state, { ...optionState, history: action.payload !== undefined });
                    updateOrAddColumnTemplate(state, { ...optionState, history: action.payload !== undefined });
                }
                if (widgetComponentProps.userSettings?.dataGranularity) {
                    widgetComponentProps.datapointParams?.forEach((datapointParam) => {
                        datapointParam.measures?.forEach((measure) => {
                            measure.timeSeries = widgetComponentProps.userSettings.dataGranularity;
                        });
                    });
                }
            }
        },
        setStacking: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            if (widgetComponentProps) {
                widgetComponentProps.unstack = action.payload;
            }
        },
        setVariationOfMetric: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            if (widgetComponentProps) {
                widgetComponentProps.variationOfMetric = action.payload;
                const optionState = selectEditorSettingsColumnsOptions(state)?.[0];
                if (optionState) {
                    updateOrAddDatapointRequest(state, { ...optionState, variation: action.payload !== undefined });
                    updateOrAddColumnOptionState(state, { ...optionState, variation: action.payload !== undefined });
                    updateOrAddColumnTemplate(state, { ...optionState, variation: action.payload !== undefined });
                }
            }
        },
        setWidgetMode: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            widgetComponentProps.mode = action.payload;
            const [entityType] = state.header.metaData?.entityType || [];
            const entityNameMeasure = defaultComparisonMeasureByEntity[entityType];
            const granularityToKeep = widgetComponentProps.userSettings?.dataGranularity;
            if (entityNameMeasure) {
                const initialState = WorkShopInitialStateByType[state.header.widgetType || 0](entityType)?.componentProps;
                widgetComponentProps.userSettings = { ...widgetComponentProps.mode === WidgetMode.Comparison
                        ? initialState?.userSettingsForComparision
                        : initialState.userSettings,
                    ...granularityToKeep && { dataGranularity: granularityToKeep } };
                updateMeasuresAndBuilders(widgetComponentProps, entityNameMeasure);
            }
        },
        setWidgetUserSettings: (state, action) => {
            if (action.payload.userSettings) {
                const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
                widgetComponentProps.userSettings = {
                    ...widgetComponentProps.userSettings,
                    ...action.payload.userSettings
                };
                if (widgetComponentProps.userSettings?.dataGranularity && widgetComponentProps.sparkline) {
                    widgetComponentProps.datapointParams?.forEach((datapointParam) => {
                        datapointParam.measures?.forEach((measure) => {
                            measure.timeSeries = widgetComponentProps.userSettings.dataGranularity;
                        });
                    });
                }
            }
        },
        setWorkshop: (state, action) => {
            if (action.payload) {
                Object.assign(state, action.payload);
            }
        },
        setWorkshopChartSeriesTypes: (state, action) => {
            const widgetComponentProps = selectEditorPreviewWidgetComponentProps(state);
            if (widgetComponentProps) {
                widgetComponentProps.chartSeriesTypes = action.payload;
            }
        },
        setWorkshopComponentProps: setWorkshopComponentPropsReducer,
        setWorkshopEditor: (state, action) => {
            if (action.payload) {
                Object.assign(state.editor, action.payload);
            }
            updateEditorPreviewWidgetComponentProps(state);
        },
        setWorkshopHeader: (state, action) => {
            if (action.payload) {
                Object.assign(state.header, action.payload);
                state.editor.preview = {
                    ...state.editor.preview,
                    widget: {
                        ...state.editor?.preview?.widget,
                        ...WorkShopInitialStateByType[state.header.widgetType || 0](action.payload.metaData?.entityType?.[0] ||
                            action.payload.metaData?.dimensionSelectorEntityType?.[0] || ""),
                        id: action.payload.id,
                        metadata: action.payload.metaData,
                        name: action.payload.name
                    }
                };
            }
        },
        setWorkshopName: (state, action) => {
            if (action.payload) {
                state.header.name = action.payload;
                state.editor.preview = {
                    ...state.editor.preview,
                    widget: {
                        ...state.editor?.preview?.widget,
                        name: action.payload
                    }
                };
            }
        },
        toggleColumnsHide: (state, action) => {
            const columnOptions = selectEditorSettingsColumnsOptions(state) ?? [];
            const optionsToToggle = columnOptions.filter((option) => action.payload.includes(option.id));
            optionsToToggle.forEach((option) => {
                option.hide = !option.hide;
                updateOrAddColumnTemplate(state, option);
            });
            const groupsToRemove = columnOptions
                .filter((option) => option.isGroup && columnOptions.every((o) => o.groupId !== option.id || o.hide))
                .map((o) => o.id);
            const childrenToRemove = columnOptions
                .filter((o) => o.groupId && groupsToRemove.includes(o.groupId))
                .map((o) => o.id);
            const totalToRemove = groupsToRemove.concat(childrenToRemove);
            deleteManyColumnOptionStates(state, totalToRemove);
            removeManyDatapointRequests(state, totalToRemove);
            deleteManyColumnTemplates(state, totalToRemove);
            removeManyDataSelectProps(state, totalToRemove);
        },
        unapplyGroupingOnColumns: unapplyGroupingOnColumnsReducer,
        ungroupColumns: (state, action) => {
            if (!action.payload) {
                return;
            }
            const settingsData = selectEditorSettingsData(state);
            if (settingsData?.columns?.options) {
                const columnToUngroup = settingsData.columns.options.filter((c) => c.groupId === action.payload);
                const columnToUngroupFields = columnToUngroup.map((c) => c.id);
                columnToUngroup.forEach((c) => {
                    c.groupId = null;
                });
                removeManyColumnTemplatesFromGroup(state, columnToUngroupFields);
                sortColumnTemplates(state);
            }
        },
        unsetColumnInVariation: unsetColumnInVariationReducer,
        updateColumn: addColumnReducer,
        updateColumns: (state, action) => {
            if (!action?.payload) {
                return;
            }
            const newColumnOptions = updateOrAddManyColumnOptionStates(state, action.payload);
            if (newColumnOptions) {
                updateOrAddManyColumnTemplates(state, newColumnOptions);
            }
        }
    }
});
export const selectDetailsPanelState = (state, type) => state.workshop.editor.detailPanels[type];
export const widgetWorkshopReducer = slice.reducer;
export const { addColumn, addSettingsGroupingOptionsDatapoints, addSettingsGroupingOptions, applyGroupingOnColumns, groupColumns, moveColumns, removeColumns, toggleColumnsHide, removeSettingsGroupingOptionsDatapoints, resetWorkshopState, setBarDatapoinOptionName, setColumnInVariation, setColumnOptionActiveAccordionTab, clearCurrentFilterValues, setColumnOptionIsChecked, setColumnOptionName, setColumnOptionSortOrder, setColumnOptionPmiIndex, setCurrentFilterValues, setDetailsPanel, setDisplayOptionsExcludeSoldAssets, setDisplayOptionsTotals, setShowNotification, setWorkshop, setWorkshopComponentProps, setWorkshopEditor, setWorkshopHeader, setWorkshopName, unapplyGroupingOnColumns, ungroupColumns, updateColumn, updateColumns, unsetColumnInVariation, setWidgetMode, setStacking, setWidgetUserSettings, setDisplayTotalRows, setSparkline, setVariationOfMetric, setWorkshopChartSeriesTypes, setColumnOptionHighchartsDisplayType } = slice.actions;
export const isUpdatePreviewWidgetAction = (action) => Boolean([
    addColumn,
    toggleColumnsHide,
    addSettingsGroupingOptions,
    addSettingsGroupingOptionsDatapoints,
    applyGroupingOnColumns,
    groupColumns,
    moveColumns,
    removeColumns,
    removeSettingsGroupingOptionsDatapoints,
    resetWorkshopState,
    setBarDatapoinOptionName,
    setColumnInVariation,
    unsetColumnInVariation,
    setColumnOptionName,
    setColumnOptionPmiIndex,
    setColumnOptionSortOrder,
    setDisplayTotalRows,
    setDetailsPanel,
    setDisplayOptionsTotals,
    setShowNotification,
    setCurrentFilterValues,
    clearCurrentFilterValues,
    setWorkshop,
    setWorkshopComponentProps,
    setWorkshopEditor,
    setWorkshopHeader,
    setWorkshopName,
    unapplyGroupingOnColumns,
    ungroupColumns,
    updateColumn,
    updateColumns,
    setWidgetMode,
    setStacking,
    setWidgetUserSettings,
    setSparkline,
    setVariationOfMetric,
    setColumnOptionHighchartsDisplayType
].find((e) => e.type === action.type));
export const isUpdateWidgetWorkshopColumnsAction = (action) => Boolean([
    addColumn,
    applyGroupingOnColumns,
    addSettingsGroupingOptions,
    addSettingsGroupingOptionsDatapoints,
    removeColumns,
    toggleColumnsHide,
    removeSettingsGroupingOptionsDatapoints,
    setColumnInVariation,
    unsetColumnInVariation,
    setCurrentFilterValues,
    clearCurrentFilterValues,
    setWorkshopEditor,
    unapplyGroupingOnColumns,
    updateColumn,
    updateColumns
].find((e) => e.type === action.type));
